import type {
  AppMenuModel,
  AppUserInfo,
  AppViewModel,
} from '../models/AppViewModel';
import { createSlice } from '@reduxjs/toolkit';
import type { PayloadAction } from '@reduxjs/toolkit';
import { appViewApi } from './appViewApi';
import authApi from './authApi';
import {
  DEPRECATED_ROUTES,
  HIDDEN_BUT_ROUTABLE_ROUTES,
  ROUTE_REMAPPINGS,
} from '@/constants/navigation';
import { collectGrantedPaths, filterAppMenu } from '@/utils/appMenuFilter';

// The prospect menu is the platform's setup wizard, built for the prospect
// experience, so the investor deprecations and remaps do not apply to it.
const toVisibleMenu = (userInfo: AppUserInfo): AppMenuModel[] =>
  userInfo.IsProspect
    ? (userInfo.Menu ?? [])
    : filterAppMenu(userInfo.Menu, DEPRECATED_ROUTES, ROUTE_REMAPPINGS);

type AppViewStateBase = {
  isLoading: boolean;
  error: string | null;
  // Sticky copy of UserInfo.IsImpersonation, re-derived on every successful
  // app-view load (false when the load carries no UserInfo). It exists because
  // getAppViewByRole.matchPending wipes UserInfo to undefined *before* the
  // query's queryFn reads state — reading UserInfo?.IsImpersonation there would
  // always be undefined, making the request reset the impersonated session on
  // every refetch (dropping the disclaimer and other impersonated data —
  // EUD-472). It survives only the pending wipe; cleared on logout.
  isImpersonation: boolean;
  // Sticky copy of UserInfo.IsTeamMembersOnlyAdvisor, with the same lifecycle as
  // isImpersonation (survives matchPending/matchRejected wiping UserInfo, cleared
  // on logout). useImpersonation reads it so a failed or in-flight refetch doesn't
  // flip a team-members-only advisor session into "restricted" impersonation.
  isTeamMembersOnlyAdvisor: boolean;
  // Effective footer disclaimer, re-derived on every successful app-view load: an
  // empty/removed disclaimer, or a load with no UserInfo, clears it (e.g.
  // switching to a role/advisor-group without one). It is preserved through
  // getAppViewByRole.matchPending so an in-flight refetch doesn't blink an
  // already-shown disclaimer out, but cleared if that refetch then fails
  // (matchRejected) or on logout (EUD-472).
  disclaimer: string;
  // Normalized HIDDEN_BUT_ROUTABLE_ROUTES the backend menu granted this user,
  // captured before DEPRECATED_ROUTES stripped them from UserInfo.Menu.
  grantedHiddenPaths: string[];
};

type AppViewState = AppViewStateBase & AppViewModel;

// Must run on the raw backend menu, before filterAppMenu strips these routes.
const grantedHiddenPathsFor = (userInfo: AppViewModel['UserInfo']) =>
  collectGrantedPaths(userInfo?.Menu, HIDDEN_BUT_ROUTABLE_ROUTES);

const initialState: AppViewState = {
  isLoading: false,
  error: null,
  isImpersonation: false,
  isTeamMembersOnlyAdvisor: false,
  disclaimer: '',
  grantedHiddenPaths: [],
  WaInfo: undefined,
  UserInfo: undefined,
};

const appViewSlice = createSlice({
  name: 'appView',
  initialState,
  reducers: {
    startLoading: (state) => {
      state.isLoading = true;
      state.error = null;
    },
    appViewReceived: (state, action: PayloadAction<AppViewModel>) => {
      state.isLoading = false;
      state.WaInfo = action.payload.WaInfo;
      state.UserInfo = action.payload.UserInfo;
      state.grantedHiddenPaths = grantedHiddenPathsFor(state.UserInfo);
      if (state.UserInfo) {
        state.UserInfo.Menu = toVisibleMenu(state.UserInfo);
      }
      state.isImpersonation = state.UserInfo?.IsImpersonation === true;
      state.isTeamMembersOnlyAdvisor =
        state.UserInfo?.IsTeamMembersOnlyAdvisor === true;
      state.disclaimer = state.UserInfo?.Disclaimer ?? '';
    },
    appViewError: (state, action: PayloadAction<string>) => {
      state.isLoading = false;
      state.error = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder.addMatcher(
      appViewApi.endpoints.getAppViewByRole.matchPending,
      (state) => {
        state.isLoading = true;
        state.error = null;
        state.UserInfo = undefined;
        state.WaInfo = undefined;
      },
    );
    builder.addMatcher(
      appViewApi.endpoints.getAppViewByRole.matchRejected,
      (state, action) => {
        // RTK Query dispatches a rejected action with meta.condition === true
        // when it skips a duplicate request (e.g. refetch() while one is in
        // flight). The real request keeps running, so this is not a failure.
        if (action.meta.condition) return;
        state.isLoading = false;
        state.UserInfo = undefined;
        // The app view is gone, so drop its disclaimer too (it was preserved
        // through matchPending for an in-flight refetch that has now failed).
        // isImpersonation is intentionally kept: it's request state, and clearing
        // it would make a retry send preventImpersonationReset=false and reset the
        // impersonated session.
        state.disclaimer = '';
        state.error = action.error.message ?? 'Unknown error';
      },
    );
    builder.addMatcher(
      appViewApi.endpoints.getAppViewByRole.matchFulfilled,
      (state, action) => {
        state.isLoading = false;
        state.error = null;
        state.WaInfo = action.payload.WaInfo;
        state.UserInfo = action.payload.UserInfo;
        state.grantedHiddenPaths = grantedHiddenPathsFor(state.UserInfo);
        if (state.UserInfo) {
          state.UserInfo.Menu = toVisibleMenu(state.UserInfo);
        }
        state.isImpersonation = state.UserInfo?.IsImpersonation === true;
        state.isTeamMembersOnlyAdvisor =
          state.UserInfo?.IsTeamMembersOnlyAdvisor === true;
        state.disclaimer = state.UserInfo?.Disclaimer ?? '';
      },
    );
    builder.addMatcher(
      authApi.endpoints.accessInvestor.matchPending,
      (state) => {
        state.isLoading = true;
        state.error = null;
      },
    );
    builder.addMatcher(
      authApi.endpoints.accessInvestor.matchRejected,
      (state, action) => {
        // A condition rejection is a skipped duplicate, not a failure.
        if (action.meta.condition) return;
        state.isLoading = false;
        state.error = action.error.message ?? 'Unknown error';
      },
    );
    builder.addMatcher(
      authApi.endpoints.accessInvestor.matchFulfilled,
      (state, action) => {
        state.isLoading = false;
        state.error = null;
        state.WaInfo = action.payload.WaInfo;
        state.UserInfo = action.payload.UserInfo;
        state.grantedHiddenPaths = grantedHiddenPathsFor(state.UserInfo);
        if (state.UserInfo) {
          state.UserInfo.Menu = toVisibleMenu(state.UserInfo);
        }
        state.isImpersonation = state.UserInfo?.IsImpersonation === true;
        state.isTeamMembersOnlyAdvisor =
          state.UserInfo?.IsTeamMembersOnlyAdvisor === true;
        state.disclaimer = state.UserInfo?.Disclaimer ?? '';
      },
    );
    builder.addMatcher(
      authApi.endpoints.accessAdvisor.matchPending,
      (state) => {
        state.isLoading = true;
        state.error = null;
      },
    );
    builder.addMatcher(
      authApi.endpoints.accessAdvisor.matchRejected,
      (state, action) => {
        if (action.meta.condition) return;
        state.isLoading = false;
        state.error = action.error.message ?? 'Unknown error';
      },
    );
    builder.addMatcher(
      authApi.endpoints.accessAdvisor.matchFulfilled,
      (state, action) => {
        state.isLoading = false;
        state.error = null;
        state.WaInfo = action.payload.WaInfo;
        state.UserInfo = action.payload.UserInfo;
        if (state.UserInfo) {
          state.UserInfo.Menu = filterAppMenu(
            state.UserInfo.Menu,
            DEPRECATED_ROUTES,
            ROUTE_REMAPPINGS,
          );
        }
        state.isImpersonation = state.UserInfo?.IsImpersonation === true;
        state.isTeamMembersOnlyAdvisor =
          state.UserInfo?.IsTeamMembersOnlyAdvisor === true;
        state.disclaimer = state.UserInfo?.Disclaimer ?? '';
      },
    );
    builder.addMatcher(
      authApi.endpoints.accessAdvisorForTeamMember.matchPending,
      (state) => {
        state.isLoading = true;
        state.error = null;
      },
    );
    builder.addMatcher(
      authApi.endpoints.accessAdvisorForTeamMember.matchRejected,
      (state, action) => {
        if (action.meta.condition) return;
        state.isLoading = false;
        state.error = action.error.message ?? 'Unknown error';
      },
    );
    builder.addMatcher(
      authApi.endpoints.accessAdvisorForTeamMember.matchFulfilled,
      (state, action) => {
        state.isLoading = false;
        state.error = null;
        state.WaInfo = action.payload.WaInfo;
        state.UserInfo = action.payload.UserInfo;
        if (state.UserInfo) {
          state.UserInfo.Menu = filterAppMenu(
            state.UserInfo.Menu,
            DEPRECATED_ROUTES,
            ROUTE_REMAPPINGS,
          );
        }
        state.isImpersonation = state.UserInfo?.IsImpersonation === true;
        state.isTeamMembersOnlyAdvisor =
          state.UserInfo?.IsTeamMembersOnlyAdvisor === true;
        state.disclaimer = state.UserInfo?.Disclaimer ?? '';
      },
    );
    builder.addMatcher(authApi.endpoints.logout.matchFulfilled, (state) => {
      state.WaInfo = undefined;
      state.UserInfo = undefined;
      state.isImpersonation = false;
      state.isTeamMembersOnlyAdvisor = false;
      state.disclaimer = '';
      state.grantedHiddenPaths = [];
    });
  },
});

export const { startLoading, appViewReceived, appViewError } =
  appViewSlice.actions;

export default appViewSlice.reducer;
